
import { Component, Input, ChangeDetectorRef, OnChanges, AfterViewInit, ElementRef, Output, EventEmitter, OnDestroy, signal, WritableSignal, inject } from '@angular/core';
import { MediaInfoResponse } from '@key-telematics/fleet-api-client';
import { AppService } from 'app/app.service';
import { TranslateService } from '@ngx-translate/core';
import { getDateFromNow } from '../../admin/entities/utils';
import { Router } from '@angular/router';
import { MetricsService } from 'app/services';

@Component({
    selector: 'key-video',
    templateUrl: './video.component.html',
    styleUrls: ['video.component.scss'],
    standalone: false
})
export class VideoComponent implements OnChanges, AfterViewInit, OnDestroy {

    @Input() asset: string;
    @Input() video: { url: string, filename: string, type: string, mimeType: string, isDeleted: boolean };
    @Input() muted = true;
    @Input() controls = false;
    @Input() download = true;
    @Input() cover = false;

    @Output() onLoaded = new EventEmitter<VideoComponent>();
    @Output() onStatusChanged = new EventEmitter<VideoComponent>();
    @Output() onProgress = new EventEmitter<VideoComponent>();
    @Output() infoUpdated = new EventEmitter<MediaInfoResponse>();

    loaded: WritableSignal<boolean> = signal(false);
    status: WritableSignal<MediaInfoResponse> = signal(null);
    requesterName: string;
    requesterDate: string;
    requesterType: string;
    changedByUser: string;
    error: WritableSignal<string> = signal('');
    videoElement: HTMLVideoElement;
    isPlaying = false;
    progress = 0;
    currentTime: string;
    durationTime: string;
    private timer: ReturnType<typeof setTimeout>;

    private currentVideoFilename = '';


    private boundHandleMediaError = this.handleMediaError.bind(this);

    get duration(): number {
        return this.videoElement.duration;
    }

    private router = inject(Router);
    private metrics = inject(MetricsService);

    constructor(
        private el: ElementRef,
        private app: AppService,
        private i18l: TranslateService,
        private ref: ChangeDetectorRef
    ) {
    }

    ngOnChanges() {
        if (this.video.filename !== this.currentVideoFilename) {
            clearTimeout(this.timer);
            this.status.set(null);
            this.currentVideoFilename = this.video.filename;
        }

        // we check the video status is not failed, otherwise the videoElement would keep looping round
        if (this.videoElement && this.status()?.status !== 'failed') {
            this.status.set(null);
            this.videoElement.load(); // force any changes to the video element to load
        }
    }

    ngOnDestroy() {
        clearTimeout(this.timer);
        this.videoElement?.removeEventListener('error', this.boundHandleMediaError, true);
    }

    ngAfterViewInit() {
        this.setupVideoElement();
    }

    setupVideoElement() {
        this.videoElement = this.el.nativeElement.querySelector('video');
        if (!this.videoElement) return;

        this.videoElement.controls = this.controls;

        this.videoElement.onplaying = () => {
            this.isPlaying = true;
            this.onStatusChanged.emit(this);
        };

        this.videoElement.onpause = () => {
            this.isPlaying = false;
            this.onStatusChanged.emit(this);
        };

        this.videoElement.onended = () => {
            this.isPlaying = false;
            this.onStatusChanged.emit(this);
        };

        this.videoElement.ontimeupdate = () => {
            this.progress = 100 * this.videoElement.currentTime / this.videoElement.duration;
            this.currentTime = this.formatTime(Math.ceil(this.videoElement.currentTime));
            this.durationTime = this.formatTime(Math.ceil(this.videoElement.duration));
            this.onProgress.emit(this);
        };

        this.videoElement.removeEventListener('error', this.boundHandleMediaError, true);
        this.videoElement.addEventListener('error', this.boundHandleMediaError, true);

        this.videoElement.onloadeddata = () => {
            // Dettach (possibly dont need this here, but no harm to keep things clean) and re-attach the error listener now that the video has loaded cleanly (from the this.videoElement.load()).
            this.videoElement.removeEventListener('error', this.boundHandleMediaError, true);
            this.videoElement.addEventListener('error', this.boundHandleMediaError, true);

            // videos must be muted on iOS in order to play multiple videos. on all other OS's we will play the sound of the first video only to avoid a mishmash of sounds.
            // NOTE: the muted attr can't be assigned in the template because of the dynamic nature of angular templates... some further reading on the subject: https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
            // this.videoElement.muted = this.matchMedia.deviceFeatures.os === DeviceFeaturesOs.IOS || index > 0;
            this.videoElement.muted = this.muted;
            this.loaded.set(true);
            this.onLoaded.emit(this);

            // if a user requests a video that is readily available e.g it has previously been requested+loaded status: 'ready'
            // we need to update the video details in the parent to reflect this as 'handleMediaError()' will not be reached
            this.handlePreviouslyRequestedLoadedVideo();
            this.ref.markForCheck();
        };

        this.ref.markForCheck();
    }

    handleMediaError(): boolean {
        this.loaded.set(true);

        if (!this.video) {
            this.error.set(this.i18l.instant('MEDIA.ERRORS.GENERIC'));
            return false;
        }

        this.updateMediaStatus();
        return true;
    }

    formatTime(time: number) {
        time = Math.ceil(time);
        const seconds = time % 60;
        const minutes = Math.floor(time / 60 % 60);
        function addZero(number) {
            return number.toString().length > 1 ? number : '0' + number;
        }
        return addZero(minutes) + ':' + addZero(seconds);
    }

    play() {
        this.videoElement.play();
    }

    pause() {
        this.videoElement.pause();
    }

    seek(type: 'start' | 'end' | 'forward' | 'backward' | 'exact', value?: number) {

        switch (type) {
            case 'start':
                this.videoElement.currentTime = 0;
                break;
            case 'end':
                this.videoElement.currentTime = this.videoElement.duration;
                break;
            case 'forward':
                this.videoElement.currentTime += value;
                break;
            case 'backward':
                this.videoElement.currentTime -= value;
                break;
            case 'exact':
                this.videoElement.currentTime = value;
                break;
        }
    }

    onDownload() {
        this.captureAnalytics();
    }

    private async updateMediaStatus() {
        try {
            const info = await this.app.api.media.getMediaInfo(this.asset, this.video.filename);

            // filename check guards against slow connections where the user switches between videos quickly
            if (info.filename === this.currentVideoFilename) {
                this.status.set(info);
                const by = info.status + 'By';
                this.changedByUser = ['deleted', 'canceled'].includes(info.status) ? info.data[by].name : '';

                if (info.requester) {
                    this.requesterName = info.requester.type !== 'device' ? info.requester.name : '';
                    this.requesterType = this.i18l.instant('ADMIN.ENTITIES.' + info.requester.type.toUpperCase()).toLowerCase();
                    this.requesterDate = getDateFromNow(info.entity.creationDate);
                }

                this.error.set(null);

                if (info.status === 'ready') {
                    this.reloadVideo();
                } else if (info.status === 'failed') {
                    this.error.set(this.i18l.instant('MEDIA.ERRORS.GENERIC'));
                    clearTimeout(this.timer);
                } else {
                    this.timer = setTimeout(() => this.updateMediaStatus(), 10e3);
                }
            } else {
                this.reloadVideo();
            }

            this.infoUpdated.emit(info);
        } catch (err) {
            this.status.set(null);
            this.error.set(this.i18l.instant(err.name === 'ForbiddenError' ? 'MEDIA.ERRORS.FORBIDDEN' : 'MEDIA.ERRORS.GENERIC'));
        } finally {
            this.ref.markForCheck();
        }
    }

    private captureAnalytics() {
        //Videos tab on live map.
        const isLiveMap = ['status', 'videos'].every(s => this.router.url.includes(s));

        if (isLiveMap) {
            this.metrics.capture('video_download', {
                screen: 'live_map',
                tab: 'videos',
                source: 'pop_up',
                device_id: ''
            });
        }
    }

    private reloadVideo() {
        this.status.set(null);
        this.error.set(null);
        // Detach the error listener before load() to prevent the transient browser error event (fired during src reset) from re-triggering handleMediaError, otherwise we get caught in an endless loop.
        this.videoElement.removeEventListener('error', this.boundHandleMediaError, true);
        // Now get the video element to reload properly.
        this.videoElement.load();
    }

    private async handlePreviouslyRequestedLoadedVideo() {
        const info = await this.app.api.media.getMediaInfo(this.asset, this.video.filename);
        this.infoUpdated.emit(info);
    }

}


